import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Bell, X } from 'lucide-react';

/**
 * Auto-registers service worker on login.
 * Shows a one-time banner prompting users to enable push notifications.
 */
export function PushNotificationPrompt() {
  const { isAuthenticated, token } = useAuth();
  const { supported, permission, isSubscribed, subscribe } = usePushNotifications(token);
  const [dismissed, setDismissed] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !supported || isSubscribed || permission === 'denied') return;
    if (localStorage.getItem('push-prompt-dismissed')) return;

    // Show prompt after a short delay so it doesn't interrupt login flow
    const timer = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, supported, isSubscribed, permission]);

  if (!show || dismissed) return null;

  const handleEnable = async () => {
    const ok = await subscribe();
    if (ok) setDismissed(true);
    else setDismissed(true);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('push-prompt-dismissed', '1');
  };

  return (
    <div
      data-testid="push-notification-prompt"
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-96 z-50 bg-card border border-border rounded-2xl p-4 shadow-xl animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-violet-600/20 flex items-center justify-center flex-shrink-0">
          <Bell className="w-5 h-5 text-violet-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-foreground">Never miss a call</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Enable push notifications to get alerted when someone calls or messages you — even when the app is closed.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="push-prompt-enable-btn"
              onClick={handleEnable}
              className="px-4 py-1.5 bg-violet-600 hover:bg-violet-500 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              Enable
            </button>
            <button
              data-testid="push-prompt-dismiss-btn"
              onClick={handleDismiss}
              className="px-4 py-1.5 bg-muted/30 hover:bg-muted/50 text-muted-foreground text-xs font-medium rounded-lg transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
        <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
